import type { OrderModel } from 'src/types/order';
import type { MediaModel } from 'src/types/media';
import type { UseFormReturn } from 'react-hook-form';

import dayjs from 'dayjs';
import { useRef, useEffect } from 'react';

import { PaymentMethodEnum } from '../types/order-form.types';

import type { BillingSchemaType, NewOrderSchemaType } from '../types/order-form.types';

interface UseOrderPreloadParams {
  currentOrder: OrderModel | undefined;
  isEditMode: boolean;
  methods: UseFormReturn<NewOrderSchemaType>;
  medias: MediaModel[];
  setBillingMethod: (method: 'email' | 'platform', preloadValues?: Partial<BillingSchemaType>) => void;
  setPaymentMethod: (method: PaymentMethodEnum) => void;
  setSearchMedia: (search: string) => void;
}

export function useOrderPreload({
  currentOrder,
  isEditMode,
  methods,
  medias,
  setBillingMethod,
  setPaymentMethod,
  setSearchMedia,
}: UseOrderPreloadParams) {
  const hasLoadedBasicData = useRef(false);
  const hasLoadedMediaData = useRef(false);

  const { setValue } = methods;

  // Phase 1: Load basic fields (no dependency on medias)
  useEffect(() => {
    if (!isEditMode || !currentOrder || hasLoadedBasicData.current) return;

    // Media search trigger
    if (currentOrder.normalDetails?.media) {
      setSearchMedia(currentOrder.normalDetails.media.name);
      setValue('mediaId', {
        value: currentOrder.normalDetails.media.id,
        label: currentOrder.normalDetails.media.name,
      });
    } else if (currentOrder.normalDetails?.mediaId) {
      const media = medias?.find((m) => m.id === currentOrder.normalDetails?.mediaId);
      if (media) {
        setSearchMedia(media.name);
        setValue('mediaId', { value: media.id, label: media.name });
      }
    }

    // Price, currency, dates
    setValue('price', currentOrder.price || 0);
    setValue('currency', currentOrder.currency ?? 0);

    if (currentOrder.limitDate) {
      setValue('limitDate', dayjs(currentOrder.limitDate));
    }

    // Category commission
    if (currentOrder.normalDetails?.extraSensibleCategory) {
      setValue('categoryCommission', currentOrder.normalDetails.extraSensibleCategory);
    }

    // URLs
    if (currentOrder.finalUrl) setValue('finalUrl', currentOrder.finalUrl);

    // Cost fields
    if (currentOrder.costPrice !== undefined) setValue('costPrice', currentOrder.costPrice);
    if (currentOrder.costPriceCurrency !== undefined && currentOrder.costPriceCurrency !== null) {
      setValue('costPriceCurrency', currentOrder.costPriceCurrency);
    } else {
      setValue('costPriceCurrency', 0);
    }

    // Discount and special fields
    if (currentOrder.discountPercentage !== undefined)
      setValue('discountPercentage', currentOrder.discountPercentage);
    if (currentOrder.internalPlatformId)
      setValue('internalPlatformId', currentOrder.internalPlatformId);
    if (currentOrder.redactionUrl) setValue('redactionUrl', currentOrder.redactionUrl);

    // Redaction
    setValue('requireRedaction', currentOrder.normalDetails?.requireRedaction || false);
    if (currentOrder.normalDetails?.wordsWrite)
      setValue('wordCount', currentOrder.normalDetails.wordsWrite);

    // Extra details
    if (currentOrder.normalDetails?.orderDetail)
      setValue('orderDetail', currentOrder.normalDetails.orderDetail);
    if (currentOrder.normalDetails?.extraH1) setValue('title', currentOrder.normalDetails.extraH1);
    if (currentOrder.normalDetails?.extraTermsToAvoid)
      setValue('extraTermsToAvoid', currentOrder.normalDetails.extraTermsToAvoid);
    if (currentOrder.normalDetails?.extraHtml)
      setValue('extraHtml', currentOrder.normalDetails.extraHtml);
    if (currentOrder.normalDetails?.extraSource)
      setValue('extraSource', currentOrder.normalDetails.extraSource);

    // Imágenes y archivo ya subidos: el componente Upload admite un string (URL/base64) como
    // value para pintar la preview; si no se tocan, buildUpdatePayload los conserva tal cual.
    if (currentOrder.normalDetails?.image) setValue('image', currentOrder.normalDetails.image);
    if (currentOrder.normalDetails?.imageTwo)
      setValue('imageTwo', currentOrder.normalDetails.imageTwo);
    if (currentOrder.normalDetails?.file) setValue('file', currentOrder.normalDetails.file);

    // Links and keywords
    // linkType usa != null (no !== undefined) a proposito: 0 (DoFollow) es un valor valido que
    // hay que preservar, pero un pedido que nunca uso el enlace 2/3/4 trae ese campo como null
    // desde el backend, y poner null explicito en el campo (en vez de dejarlo undefined) es lo
    // que disparaba el error de validacion "Expected 0 | 1 | 2, received null" al abrir el
    // formulario de edicion sin haber tocado nada.
    const nd = currentOrder.normalDetails;
    if (nd?.url1) setValue('url1', nd.url1);
    if (nd?.keyword1) setValue('keyword1', nd.keyword1);
    if (nd?.linkType1 != null) setValue('linkType1', nd.linkType1);
    if (nd?.url2) setValue('url2', nd.url2);
    if (nd?.keyword2) setValue('keyword2', nd.keyword2);
    if (nd?.linkType2 != null) setValue('linkType2', nd.linkType2);
    if (nd?.url3) setValue('url3', nd.url3);
    if (nd?.keyword3) setValue('keyword3', nd.keyword3);
    if (nd?.linkType3 != null) setValue('linkType3', nd.linkType3);
    if (nd?.url4) setValue('url4', nd.url4);
    if (nd?.keyword4) setValue('keyword4', nd.keyword4);
    if (nd?.linkType4 != null) setValue('linkType4', nd.linkType4);

    // Billing method
    // Se pasan los valores directamente a setBillingMethod (en vez de billingMethods.setValue
    // suelto) porque cambiar de modo dispara un reset a los defaults de ese modo (ver
    // use-billing-form.ts); ese reset se resuelve en el render siguiente a este efecto, así que
    // un setValue aquí quedaría pisado por él.
    if (currentOrder.platformId && currentOrder.platform) {
      setBillingMethod('platform', {
        platformId: { value: currentOrder.platformId, label: currentOrder.platform } as any,
      });
    } else {
      setBillingMethod(
        'email',
        currentOrder.invoice
          ? {
              customerId: currentOrder.invoice.id || undefined,
              firstName: currentOrder.invoice.name || '',
              lastName: currentOrder.invoice.surname || '',
              company: currentOrder.invoice.company || '',
              email: currentOrder.normalDetails?.externalEmail || currentOrder.user?.email || '',
              phone: currentOrder.invoice.phone || '',
              nif: currentOrder.invoice.nif || '',
              country: currentOrder.invoice.country || '',
              city: currentOrder.invoice.city || '',
              postalCode: currentOrder.invoice.postalCode || '',
              address: currentOrder.invoice.address || '',
            }
          : undefined
      );
    }

    // Payment method
    if (currentOrder.paymentMethod !== undefined) {
      if (currentOrder.paymentMethod === 2) {
        setPaymentMethod(PaymentMethodEnum.PayPal);
      } else if (currentOrder.paymentMethod === 3) {
        setPaymentMethod(PaymentMethodEnum.WiseTransfer);
      } else {
        setPaymentMethod(PaymentMethodEnum.Pending);
      }
    }

    hasLoadedBasicData.current = true;
  }, [
    isEditMode,
    currentOrder,
    setValue,
    setBillingMethod,
    setPaymentMethod,
    setSearchMedia,
    medias,
  ]);

  // Phase 2: Load media-dependent data when medias are available
  useEffect(() => {
    if (
      !isEditMode ||
      !currentOrder ||
      !medias?.length ||
      !hasLoadedBasicData.current ||
      hasLoadedMediaData.current
    )
      return;

    if (currentOrder.normalDetails?.mediaId) {
      const fullMediaObject = medias.find((m) => m.id === currentOrder.normalDetails?.mediaId);

      if (fullMediaObject) {
        setValue('mediaId', {
          value: fullMediaObject.id,
          label: fullMediaObject.name,
        });

        // Load category after media is ready
        if (currentOrder.normalDetails?.categoryId) {
          setTimeout(() => {
            setValue('categoryId', currentOrder.normalDetails!.categoryId!, {
              shouldValidate: false,
              shouldDirty: false,
              shouldTouch: false,
            });
          }, 500);
        }

        // Re-set currency values after media auto-fill to preserve original values
        setTimeout(() => {
          setValue('currency', currentOrder.currency ?? 0);
          if (
            currentOrder.costPriceCurrency !== undefined &&
            currentOrder.costPriceCurrency !== null
          ) {
            setValue('costPriceCurrency', currentOrder.costPriceCurrency);
          }
        }, 600);
      }
    }

    hasLoadedMediaData.current = true;
  }, [isEditMode, currentOrder, medias, setValue]);
}
